import React, { useCallback, useEffect, useState } from 'react';
import Editor, { useMonaco } from '@monaco-editor/react';
import CalendarUtils from 'services/CalendarUtils';
import _ from 'lodash';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import { useAppSelector } from 'careand-redux/store';
import socket from 'websockets/socket';

const LANG_NAME = 'careAndLang';
//disabled -- this really fucks up performance in the Backend. We need a separate spellcheck service if we want to enable this.
var waitingSpellcheck = true;

const ADD_TO_DICTIONARY = 'Add to dictionary';
const NO_SUGGESTIONS = 'No suggestions';

const MonacoTest = ({ id, value, onChange, metrics }) => {
  const monaco = useMonaco();
  const [socketInstance, setSocketInstance] = useState();

  const socketConnection = useAppSelector((state) => state.resource.socketConnection);

  const debouncedRequestSpellCheck = useCallback(_.debounce(requestSpellCheck, 350), [socket]);

  useEffect(() => {
    if (socketInstance && value) {
      debouncedRequestSpellCheck(value);
    }
  }, [debouncedRequestSpellCheck, value, socketInstance]);

  function requestSpellCheck(value) {
    if (socketInstance && !waitingSpellcheck && !!value) {
      waitingSpellcheck = true;
      let processedString = value.replace(/[^a-zA-Z ]/g, ' ');
      let words = _.uniq(processedString.split(/(\s+)/).filter((word) => word.trim().length > 2));
      socketInstance.emit('spellcheck', words);
    }
  }

  function addToDictionary(word, value) {
    if (socketInstance) {
      socketInstance.emit('spellcheck-add-word', { word });
      debouncedRequestSpellCheck(value);
    }
  }

  function receivedSpellCheck(errors) {
    waitingSpellcheck = false;
    if (monaco?.editor) {
      let models = monaco.editor.getModels();
      let model = models[0];
      let markers = [];
      errors.forEach(({ word, suggestions = [] }) => {
        if (model) {
          let matches = model.findMatches(word);
          markers = [
            ...markers,
            ...matches.map((match) => {
              return {
                ...match.range,
                message: suggestions.length > 0 ? suggestions.join(', ') : NO_SUGGESTIONS,
                severity: monaco.MarkerSeverity.Error,
              };
            }),
          ];
        }
      });
      monaco.editor.setModelMarkers(model, 'owner', markers);
    }
  }

  useEffect(() => {
    let instance = socket.getSocket();
    if (socketConnection === 'connected' && instance) {
      setSocketInstance(instance);
      instance.on('spellcheck-result', (errors) => {
        receivedSpellCheck(errors);
      });
    }

    return () => {
      if (instance) instance.removeListener('spellcheck');
      setSocketInstance();
    };
  }, [socketConnection, monaco]);

  useEffect(() => {
    function provideCompletionItems(model, position, context, token) {
      let suggestions = [
        ...SUGGESTIONS.map((suggestion) => {
          return {
            ...suggestion,
            kind: monaco.languages.CompletionItemKind.Snippet,
          };
        }),
        ...(metrics || [])
          .filter((metricData) => metricData.metric.last_value)
          .map((metricData) => {
            let { last_value, metric } = metricData;
            let { value1, value2, date } = last_value;
            let { name, units } = metric;
            let value = value1;
            if (value2) value += `/${value2}`;
            let dateString = CalendarUtils.format(date);
            return {
              label: `${name} metric (patient data)`,
              kind: monaco.languages.CompletionItemKind.Snippet,
              insertText: `${name}: ${value} ${units} (${dateString})`,
            };
          }),
      ];
      return { suggestions };
    }

    let registration;

    if (monaco) {
      let exists = !!monaco.languages.getLanguages().find(({ id }) => id === LANG_NAME);
      if (!exists) {
        function resolveCodeAction(codeAction, token) {
          if (codeAction.title === ADD_TO_DICTIONARY) {
            let { startColumn, startLineNumber, endLineNumber, endColumn } =
              codeAction.diagnostics[0] || {};
            let models = monaco.editor.getModels();
            let range = new monaco.Range(startLineNumber, startColumn, endLineNumber, endColumn);
            let value = models[0].getValueInRange(range);
            if (value) {
              addToDictionary(value, models[0].getValue());
            }
          }
        }

        function provideCodeActions(model, range, context, token) {
          const actions = _.flatten(
            context.markers.map((error) => {
              let suggestions = error.message !== NO_SUGGESTIONS ? error.message.split(', ') : [];

              return [
                suggestions.map((suggestion) => {
                  return {
                    title: suggestion,
                    diagnostics: [error],
                    kind: 'quickfix',
                    edit: {
                      edits: [{ resource: model.uri, edit: { range: error, text: suggestion } }],
                    },
                  };
                }),
                {
                  title: '',
                  diagnostics: [error],
                  kind: 'quickfix',
                },
                {
                  title: ADD_TO_DICTIONARY,
                  diagnostics: [error],
                  kind: 'quickfix',
                },
              ];
            }),
          );
          return {
            actions,
            dispose: () => {},
          };
        }

        monaco.languages.register({ id: LANG_NAME });
        monaco.languages.setMonarchTokensProvider(LANG_NAME, CAREAND_LANG);
        monaco.languages.registerCodeActionProvider(LANG_NAME, {
          resolveCodeAction,
          provideCodeActions,
        });
        requestSpellCheck(value);
      }
      registration = monaco.languages.registerCompletionItemProvider(LANG_NAME, {
        provideCompletionItems,
      });
    }

    return () => {
      if (registration) registration.dispose();
    };
  }, [monaco, metrics]);

  return (
    <div id={id} style={{ height: '400px' }}>
      <Editor
        height="400px"
        defaultLanguage={LANG_NAME}
        defaultValue={value}
        value={value}
        onChange={onChange}
        options={{
          glyphMargin: false,
          folding: false,
          lineDecorationsWidth: 5,
          lineNumbersMinChars: 0,
          lineHeight: 20,
          wordWrap: 'on',
          matchBrackets: 'never',
          lineNumbers: false,
          extraEditorClassName: 'careand-editor',
          contextmenu: false,
          minimap: { enabled: false },
          fontSize: 16,
          acceptSuggestionOnEnter: 'off',
        }}
      />
    </div>
  );
};

export default MonacoTest;

const CAREAND_LANG = {
  keywords: [],

  typeKeywords: [],

  operators: [],

  symbols: /[:]+/,

  escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,

  tokenizer: {
    root: [
      // identifiers and keywords
      //[/[a-z_$][\w$]*/, {
      //  cases: {
      //    '@typeKeywords': 'keyword',
      //    '@keywords': 'keyword',
      //    '@default': 'identifier'
      //  }
      //}],
      //[/[A-Z][\w\$]*/, 'type.identifier'],  // to show class names nicely
      // whitespace
      //{include: '@whitespace'},
      // delimiters and operators
      //[/[{}()\[\]]/, '@brackets'],
      //[/[<>](?!@symbols)/, '@brackets'],
      //[/@symbols/, {
      //        cases: {
      //        '@operators': 'operator',
      //  '@default': ''
      //  }
      // }
      //],
      // @ annotations.
      // As an example, we emit a debugging log message on these tokens.
      // Note: message are supressed during the first load -- change some lines to see them.
      //ALL ACTIVE HERE
      //[/@\s*[a-zA-Z_\$][\w\$]*/, {token: 'annotation', log: 'annotation token: $0'}],
      // numbers
      //[/\d*\.\d+([eE][\-+]?\d+)?/, 'number.float'],
      //[/0[xX][0-9a-fA-F]+/, 'number.hex'],
      //[/\d+/, 'number'],
      // delimiter: after number because of .\d floats
      //[/[;,.]/, 'delimiter'],
      // strings
      //[/"([^"\\]|\\.)*$/, 'string.invalid'],  // non-teminated string
      //[/"/, {token: 'string.quote', bracket: '@open', next: '@string'}],
      // characters
      //[/'[^\\']'/, 'string'],
      //[/(')(@escapes)(')/, ['string', 'string.escape', 'string']],
      //[/'/, 'string.invalid']
    ],

    comment: [],

    string: [],

    whitespace: [],
  },
};

const SUGGESTIONS = [
  {
    label: 'ROS GEN (snippet)',
    insertText:
      'ROS GEN: no fatigue, fever/chills, unexplained weight gain/loss, decreased appetite, or night sweats ',
  },
  {
    label: 'ROS HEENT (snippet)',
    insertText:
      'ROS HEENT: no vision change, eye pain/edema/discharge, difficulty swallowing, ear pain/edema/discharge, hearing loss, rhinorrhea or rhinitis, pharyngitis, or nuchal rigidity ',
  },
  {
    label: 'ROS CV (snippet)',
    insertText: 'ROS CV: no chest pain/tightness, palpitations, pre/syncope, or edema ',
  },
  {
    label: 'ROS RESP (snippet)',
    insertText: 'ROS RESP: no dyspnea, cough, wheeze, or SOBOE ',
  },
  {
    label: 'ROS GI (snippet)',
    insertText:
      'ROS GI: no abdo pain, acid reflux, N/V/D, constipation, hematochezia, clay colored/black/mucus stools, rectal pain, or food intolerance ',
  },
  {
    label: 'ROS URO (snippet)',
    insertText:
      'ROS URO: no cloudy/foul smelling urine, frequency/ urgency, dysuria, hematuria, sensation of incomplete emptying ',
  },
  {
    label: 'ROS GYNE (snippet)',
    insertText:
      'ROS GYNE: no abn dcx, lesions, dyspareunia, dysmenorrhea or post-coital bleeding, no irreg menses or heavy bleeding/spotting/pain/clots ',
  },
  {
    label: 'ROS MALE GENT (snippet)',
    insertText:
      'ROS MALE GENT: No LUTS, lesions, painful ejaculation, penile dcx, ED, testicular pain/heaviness/edema/nodule',
  },
  {
    label: 'ROS MSK (snippet)',
    insertText:
      'ROS MSK: no hx trauma/fall, pain, decreased ROM, change in strength/power, edema/erythema, or crepitis',
  },
  {
    label: 'ROS INTEG (snippet)',
    insertText:
      'ROS INTEG: no worrisome moles/skin lesions, skin/hair/nail changes, or skin coolness/warmth ',
  },
  {
    label: 'ROS ENDO (snippet)',
    insertText:
      'ROS ENDO: no increased thirst/urination, cold/heat intolerance, or changes in sex drive ',
  },
  {
    label: 'ROS NEURO (snippet)',
    insertText:
      'ROS NEURO: no headache, paresthesia, trauma, confusion, dizziness, or balance/coordination concerns ',
  },
  {
    label: 'ROS PSYCH (snippet)',
    insertText:
      'ROS PSYCH: no altered sleep, acute stress, mood changes, anxiety, hallucinations, compulsions, or SH/SI/HI ',
  },
  {
    label: 'Gyne Hx (snippet)',
    insertText: `Age of Menarche / Menopause:
GTPAL: nulliaprous
LMP:
Last pap:
Frequency:
Duration:
Volume:
Colour/consistency:
Intercycle Bleeding:
Symptoms: pain, n/v/d, HA, abn dcx, mood changes, breast tenderness, post-coital bleeding, dyspareunia
Contraception use:`,
  },
  {
    label: 'STI (stamp)',
    insertText: `S/
CC: STI Screen
Associated symptoms:

PMHx:
Previous STIs Hx:
Meds- recent abx use:
Vaccines- Gardasil:

Sexual Hx:
# partners in last 6 mos:
Male/female/both
Monogamous/non-monogomous:
Intercourse: vaginal, oral, anal (penetrative/receptive)
Condom use (%):
Partners w/ STIs:  
Last Pap:
Birth control:
LMP:
Chance of pregnancy:

SocHx:
Rec drugs: IVDU?
Any recent tattoos/piercings?:

ROS:
GEN: no fatigue, fever/chills, unexplained weight gain/loss, decreased appetite, or night sweats
HEENT: no sore throat, lymphadenopathy
INTEG: no rashes, chancre
GYNE: no abn dcx, lesions, dyspareunia, dysmenorrhea or post-coital bleeding, no irreg menses or heavy bleeding/spotting/pain/clots
ROS MALE GENT: No LUTS, lesions, painful ejaculation, penile dcx, ED, testicular pain/heaviness/edema/nodule
URO: no cloudy/foul smelling urine, frequency/ urgency, dysuria, hematuria, sensation of incomplete emptying

O/
Gen: appears well kempt and stable

GI/GU: abdo and pelvis flat, no CVA tenderness

Gyne:
-ext genitalia normal; speculum- normal vaginal mucosa, no abnormal appearance/odor to discharge;
-cervix pink/ectropion, non-friable

Uro:
-meatus no discharge; glans and shaft no lesions;
-no scrotal, testicular, epididymal, or spermatic cord swelling, lesions, masses, tenderness or deformities

Rectal:
-no dcx, lesions, hemorrhoids, or fissures;
-DRE- anal sphincter normal tone, no pain, spasm, or masses; prostate normal

A/
STI Screen

P/
Urine; cervical/anal/pharyngeal swab for gonorrhea/chlamydia
Bloodwork requisition provided for HIV, HAV, HBV, HCV, VDRL, HSV
Discussed risk reduction- safer sex and consistent condom use
Complications, warning signs, and plan discussed
FUP with test results in app and if symptoms persist or worsen`,
  },
  {
    label: 'Mantoux (stamp)',
    insertText: `S/ 
TB test required for school/work 
1/2 step required 
 
PmHx 
Dx-  No hx TB or HIV 
Country of origin-  
Vaccines- no BCG vaccine 

No recent travel 
No hx adverse reaction to vaccines
No sob, cough, wheeze, fever/chills, weight loss, night sweats, anorexia 
 
O/ 
Gen- appears well and stable 
 
A/P
1.Mantoux test; step 1/2
- 5 TU (0.1ml) injected intradermally in R/L forearm 
- Discussed possible adverse effects 
- Patient here x10 min- no adverse reaction 
- RTC 48-72 h for reading 
- RTC 1 week from today for step 2 
- FUP prn`,
  },
  {
    label: 'SOCHx (snippet)',
    insertText:
      'SOCHX:\nETOH:  \n' +
      'Smoking: \n' +
      'Rec drugs: \n' +
      'Diet: \n' +
      'Exercise: \n' +
      'Work: \n' +
      'Living: \n' +
      'Social/financial support: \n' +
      'Pets:  \n' +
      'Sex: # partner, male/female, last sti check- ',
  },
  {
    label: 'Urine R&M In-office (snippet)',
    insertText: 'Leuk -\nNit -\nBlo -\npH -\nProt -\nKet -\nGlu -',
  },
  {
    label: 'PMhx (snippet)',
    insertText:
      'PMhx:\n' +
      'Dx- \n' +
      'Medications- \n' +
      'Injury: \n' +
      'Allergies: \n' +
      'Diet- \n' +
      'Substances- \n' +
      'Stress- \n\n',
  },
  {
    label: 'Appears well kempt and stable',
    insertText: 'Appears well kempt and stable\n',
  },
  {
    label: 'unremarkable',
    insertText: 'unremarkable',
  },
  {
    label: 'deferred (telemedicine)',
    insertText: 'deferred - telemedicine assessment; client aware assessment limited\n',
  },
  {
    label: 'Discussed above',
    insertText: 'Discussed above\n',
  },
  {
    label: 'FU as needed',
    insertText: 'FU as needed\n',
  },
  {
    label: 'FU if symptoms worsen or as needed',
    insertText: 'FU if symptoms worsen or as needed\n',
  },
  {
    label: 'Pain Assessment (snippet)',
    insertText:
      'PAIN \n' +
      'Onset- \n' +
      'Duration-  \n' +
      'Location-  \n' +
      'Radiating-  \n' +
      'Intensity-  \n' +
      'Quality-  \n' +
      'Timing- \n' +
      'Alleviated by- \n' +
      'Aggravated by- \n\n',
  },
];

export const NoteCommands = () => {
  return (
    <div style={{ width: '600px' }}>
      {SUGGESTIONS.map(({ label, insertText }) => {
        return (
          <UncontrolledCollapse title={label} key={label}>
            <pre style={{ marginTop: '20px' }}>{insertText}</pre>
          </UncontrolledCollapse>
        );
      })}
    </div>
  );
};
